import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, Search, Edit, Trash2, XCircle, MapPin } from "lucide-react";
import { toast } from "sonner";

const formVacio = () => ({ cliente_id: "", medicamento_id: "", dosis_diaria: "", dias_anticipacion: "5" });

function formatFecha(iso) {
  if (!iso) return null;
  return iso.split("T")[0].split("-").reverse().join("/");
}

export default function Inscripciones() {
  const [inscripciones, setInscripciones] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [medicamentos, setMedicamentos] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ dosis_diaria: "", dias_anticipacion: "" });
  const [form, setForm] = useState(formVacio());

  const load = async () => {
    try {
      const [inscRes, cliRes, medRes] = await Promise.all([
        axios.get(`${API}/inscripciones`),
        axios.get(`${API}/clientes`),
        axios.get(`${API}/medicamentos`),
      ]);
      setInscripciones(inscRes.data);
      setClientes(cliRes.data.filter(c => c.activo));
      setMedicamentos(medRes.data);
    } catch { toast.error("Error al cargar inscripciones"); }
  };

  useEffect(() => { load(); }, []);

  // Group inscripciones by cliente_id
  const grouped = inscripciones.reduce((acc, i) => {
    const key = i.cliente_id;
    if (!acc[key]) {
      acc[key] = {
        cliente_id: key,
        cliente_nombre: i.cliente_nombre,
        cliente_cedula: i.cliente_cedula,
        cliente_telefono: i.cliente_telefono,
        cliente_zona: i.cliente_zona,
        inscripciones: [],
      };
    }
    acc[key].inscripciones.push(i);
    return acc;
  }, {});

  const groups = Object.values(grouped);

  const q = search.toLowerCase();
  const filteredGroups = q
    ? groups.filter(g =>
        g.cliente_nombre.toLowerCase().includes(q) ||
        (g.cliente_cedula || "").includes(q) ||
        g.inscripciones.some(i => (i.medicamento_display || "").toLowerCase().includes(q))
      )
    : groups;

  const set = (f) => (e) => setForm(prev => ({ ...prev, [f]: e.target.value }));

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/inscripciones`, {
        cliente_id: form.cliente_id,
        medicamento_id: form.medicamento_id,
        dosis_diaria: parseFloat(form.dosis_diaria),
        dias_anticipacion: parseInt(form.dias_anticipacion),
      });
      toast.success("Inscripción creada");
      setOpen(false);
      setForm(formVacio());
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear inscripción");
    }
  };

  const handleEdit = (insc) => {
    setEditing(insc);
    setEditForm({ dosis_diaria: String(insc.dosis_diaria), dias_anticipacion: String(insc.dias_anticipacion) });
    setEditOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/inscripciones/${editing.id}`, {
        dosis_diaria: parseFloat(editForm.dosis_diaria),
        dias_anticipacion: parseInt(editForm.dias_anticipacion),
      });
      toast.success("Inscripción actualizada");
      setEditOpen(false);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar");
    }
  };

  const handleInactivar = async (id) => {
    try {
      await axios.put(`${API}/inscripciones/${id}`, { activo: false });
      toast.success("Inscripción inactivada");
      load();
    } catch { toast.error("Error al inactivar"); }
  };

  const handleDelete = async (id, nombre, med) => {
    if (!window.confirm(`¿Eliminar la inscripción de ${nombre} — ${med}?`)) return;
    try {
      await axios.delete(`${API}/inscripciones/${id}`);
      toast.success("Inscripción eliminada");
      load();
    } catch { toast.error("Error al eliminar"); }
  };

  const clienteSeleccionado = clientes.find(c => c.id === form.cliente_id);
  const hoy = new Date().toISOString().split("T")[0];
  const totalActivas = inscripciones.filter(i => i.activo).length;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Inscripciones</h1>
          <p className="text-sm text-slate-500">
            {filteredGroups.length} clientes · {totalActivas} medicamentos activos
          </p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setForm(formVacio()); }}>
          <DialogTrigger asChild>
            <Button className="bg-red-700 hover:bg-red-800 text-white">
              <Plus className="w-4 h-4 mr-2" /> Nueva Inscripción
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Nueva Inscripción</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <Label>Cliente *</Label>
                <select
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm mt-0.5"
                  value={form.cliente_id} onChange={set("cliente_id")} required
                >
                  <option value="">Selecciona un cliente</option>
                  {clientes.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.nombre} {c.apellidos || ""} {c.cedula ? `(${c.cedula})` : ""}
                    </option>
                  ))}
                </select>
                {clienteSeleccionado && (
                  <p className="text-xs text-slate-400 mt-1">
                    Zona: {clienteSeleccionado.zona || "Sin zona"} · Tel: {clienteSeleccionado.telefono}
                  </p>
                )}
              </div>
              <div>
                <Label>Medicamento *</Label>
                <select
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm mt-0.5"
                  value={form.medicamento_id} onChange={set("medicamento_id")} required
                >
                  <option value="">Selecciona un medicamento</option>
                  {medicamentos.map(m => (
                    <option key={m.id} value={m.id}>{m.nombre} {m.concentracion || ""}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Dosis diaria (past./día) *</Label>
                  <Input type="number" min="0.5" step="0.5" placeholder="Ej: 1"
                    value={form.dosis_diaria} onChange={set("dosis_diaria")} required />
                </div>
                <div>
                  <Label>Días de anticipación</Label>
                  <Input type="number" min="1" max="30" placeholder="5"
                    value={form.dias_anticipacion} onChange={set("dias_anticipacion")} />
                  <p className="text-xs text-slate-400 mt-1">Avisar X días antes</p>
                </div>
              </div>
              {form.dosis_diaria && (
                <div className="bg-blue-50 rounded-lg px-3 py-2 text-xs text-blue-700">
                  Con {form.dosis_diaria} past./día: caja de 30 dura {Math.floor(30 / form.dosis_diaria)} días · caja de 60 dura {Math.floor(60 / form.dosis_diaria)} días
                </div>
              )}
              <Button type="submit" className="w-full bg-red-700 hover:bg-red-800">Crear Inscripción</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
        <Input
          placeholder="Buscar por nombre, cédula o medicamento..."
          value={search} onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Grouped list */}
      {filteredGroups.length === 0 ? (
        <div className="text-center py-16 text-slate-400 text-sm">No hay inscripciones.</div>
      ) : (
        <div className="space-y-3">
          {filteredGroups.map(group => (
            <Card key={group.cliente_id} className="border-0 shadow-sm overflow-hidden">
              {/* Client header */}
              <div className="flex items-center gap-3 px-5 py-3 bg-slate-50 border-b border-slate-100">
                <div className="flex-1 min-w-0">
                  <span className="font-semibold text-slate-800 text-sm">{group.cliente_nombre}</span>
                  {group.cliente_cedula && (
                    <span className="ml-2 text-xs text-slate-400">{group.cliente_cedula}</span>
                  )}
                  {group.cliente_telefono && (
                    <span className="ml-2 text-xs text-slate-400">· {group.cliente_telefono}</span>
                  )}
                  {group.cliente_zona && (
                    <span className="ml-2 inline-flex items-center gap-0.5 text-xs text-slate-400">
                      · <MapPin className="w-3 h-3 inline" /> {group.cliente_zona}
                    </span>
                  )}
                </div>
                <span className="text-xs text-slate-400 shrink-0">
                  {group.inscripciones.filter(i => i.activo).length} medicamento{group.inscripciones.filter(i => i.activo).length !== 1 ? "s" : ""}
                </span>
              </div>

              {/* Medications rows */}
              <CardContent className="p-0">
                {group.inscripciones.map((insc, idx) => {
                  const proxima = insc.proxima_notificacion_fecha;
                  const vencida = proxima && proxima <= hoy;
                  return (
                    <div
                      key={insc.id}
                      className={`flex items-center gap-3 px-5 py-2.5 text-sm ${
                        idx < group.inscripciones.length - 1 ? "border-b border-slate-50" : ""
                      } ${!insc.activo ? "opacity-40" : ""}`}
                    >
                      {/* Medication name */}
                      <div className="w-48 shrink-0">
                        <span className="text-slate-700 font-medium">{insc.medicamento_display}</span>
                        {insc.ultima_compra_producto && (
                          <p className="text-xs text-slate-400 mt-0.5 truncate" title={insc.ultima_compra_producto}>
                            {insc.ultima_compra_producto}
                          </p>
                        )}
                        {!insc.ultima_compra_producto && insc.marca_comercial && (
                          <p className="text-xs text-blue-400 mt-0.5 truncate" title={insc.marca_comercial}>
                            {insc.marca_comercial}
                          </p>
                        )}
                      </div>

                      {/* Dosis */}
                      <div className="w-20 shrink-0 text-center text-slate-500 text-xs">
                        {insc.dosis_diaria} past./día
                      </div>

                      {/* Última compra */}
                      <div className="w-44 shrink-0 text-xs text-slate-400">
                        {insc.ultima_compra_fecha ? (
                          <span>
                            {formatFecha(insc.ultima_compra_fecha)}
                            {insc.ultima_compra_cantidad && (
                              <span className="ml-1 text-slate-300">({insc.ultima_compra_cantidad} past.)</span>
                            )}
                          </span>
                        ) : (
                          <span className="text-slate-300">Sin registros</span>
                        )}
                      </div>

                      {/* Próx. notif */}
                      <div className="w-32 shrink-0">
                        {proxima ? (
                          <span className={`text-xs font-medium ${vencida ? "text-red-600" : "text-slate-600"}`}>
                            {formatFecha(proxima)}
                            {vencida && <span className="ml-1 text-xs">(HOY)</span>}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-300">Pendiente ERP</span>
                        )}
                      </div>

                      {/* Estado */}
                      <div className="w-20 shrink-0">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          insc.activo ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"
                        }`}>
                          {insc.activo ? "Activa" : "Inactiva"}
                        </span>
                      </div>

                      {/* Acciones */}
                      <div className="flex gap-1 ml-auto shrink-0">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(insc)}
                          className="text-blue-600 hover:bg-blue-50 h-7 w-7 p-0" title="Editar dosis">
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                        {insc.activo && (
                          <Button variant="ghost" size="sm" onClick={() => handleInactivar(insc.id)}
                            className="text-orange-500 hover:bg-orange-50 h-7 w-7 p-0" title="Inactivar">
                            <XCircle className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm"
                          onClick={() => handleDelete(insc.id, group.cliente_nombre, insc.medicamento_display)}
                          className="text-red-600 hover:bg-red-50 h-7 w-7 p-0" title="Eliminar">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={(v) => { setEditOpen(v); if (!v) setEditing(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Editar Inscripción</DialogTitle>
          </DialogHeader>
          {editing && (
            <form onSubmit={handleUpdate} className="space-y-3">
              <p className="text-sm text-slate-600">
                <strong>{editing.cliente_nombre}</strong> — {editing.medicamento_display}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Dosis diaria (past./día)</Label>
                  <Input type="number" min="0.5" step="0.5"
                    value={editForm.dosis_diaria}
                    onChange={(e) => setEditForm(f => ({ ...f, dosis_diaria: e.target.value }))}
                    required />
                </div>
                <div>
                  <Label>Días anticipación</Label>
                  <Input type="number" min="1" max="30"
                    value={editForm.dias_anticipacion}
                    onChange={(e) => setEditForm(f => ({ ...f, dias_anticipacion: e.target.value }))} />
                </div>
              </div>
              <Button type="submit" className="w-full bg-red-700 hover:bg-red-800">Actualizar</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
